import { useState } from 'react';
import { Link, NavLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Menu, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const navigation = [
  { name: 'nav.home', href: '/' },
  { name: 'nav.about', href: '/about' },
  { name: 'nav.services', href: '/services' },
  // { name: 'nav.investors', href: '/investors' }, // HIDDEN UNTIL LAUNCH
  { name: 'nav.contact', href: '/contact' },
];

const softwareLinks = [
  { name: 'LiberaGPT', href: 'https://www.liberagpt.com' },
  { name: 'AISTRYX', href: 'https://www.aistryx.com' },
  { name: 'Questract', href: 'https://www.questract.com' },
];

function SoftwareDropdown() {
  const [open, setOpen] = useState(false);

  return (
    <div
      className="relative"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <button
        type="button"
        className="flex items-center gap-1 px-4 py-2 text-sm font-medium transition-colors hover:text-white"
        style={{ color: '#e0e0e0' }}
        aria-expanded={open}
      >
        Software
        <svg
          className={`w-3.5 h-3.5 transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2}
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            transition={{ duration: 0.15 }}
            className="absolute top-full left-0 mt-1 w-44 rounded-md border border-gray-700 shadow-lg overflow-hidden"
            style={{ backgroundColor: '#222222' }}
          >
            {softwareLinks.map((link) => (
              <a
                key={link.name}
                href={link.href}
                target="_blank"
                rel="noopener noreferrer"
                className="block px-4 py-2.5 text-sm font-medium transition-colors hover:text-white hover:bg-gray-700"
                style={{ color: '#e0e0e0' }}
              >
                {link.name}
              </a>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function MobileSoftwareMenu() {
  const [open, setOpen] = useState(false);

  return (
    <div>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="flex items-center justify-between w-full px-4 py-3 text-base font-medium transition-colors hover:text-white"
        style={{ color: '#e0e0e0' }}
      >
        Software
        <svg
          className={`w-4 h-4 transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2}
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="overflow-hidden pl-4 border-l border-gray-700 ml-4"
          >
            {softwareLinks.map((link) => (
              <a
                key={link.name}
                href={link.href}
                target="_blank"
                rel="noopener noreferrer"
                className="block px-4 py-2.5 text-sm font-medium transition-colors hover:text-white"
                style={{ color: '#e0e0e0' }}
              >
                {link.name}
              </a>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default function Header() {
  const { t } = useTranslation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  return (
    <header className="fixed top-0 left-0 right-0 z-50" style={{ backgroundColor: '#2a2a2a' }}>
      <nav className="border-b border-gray-700">
        <div className="container-wide">
          <div className="flex items-center justify-between h-16 lg:h-20">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-3">
              <div className="flex flex-col" style={{ marginTop: '-4px' }}>
                <span className="font-logo tracking-tight text-white text-[37px]">
                  5N6
                </span>
                <span className="text-[10px] tracking-widest uppercase -mt-1" style={{ color: '#e0e0e0' }}>
                  See clearly. Act decisively.
                </span>
              </div>
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex lg:items-center lg:gap-1">
              {navigation.slice(0, 2).map((item) => (
                <NavLink
                  key={item.name}
                  to={item.href}
                  className={({ isActive }) =>
                    `px-4 py-2 text-sm font-medium transition-colors ${
                      isActive
                        ? 'text-white'
                        : 'hover:text-white'
                    }`
                  }
                  style={({ isActive }) => ({
                    color: isActive ? '#ffffff' : '#e0e0e0'
                  })}
                >
                  {t(item.name)}
                </NavLink>
              ))}

              {/* Software Dropdown — after About */}
              <SoftwareDropdown />

              {navigation.slice(2).map((item) => (
                <NavLink
                  key={item.name}
                  to={item.href}
                  className={({ isActive }) =>
                    `px-4 py-2 text-sm font-medium transition-colors ${
                      isActive
                        ? 'text-white'
                        : 'hover:text-white'
                    }`
                  }
                  style={({ isActive }) => ({
                    color: isActive ? '#ffffff' : '#e0e0e0'
                  })}
                >
                  {t(item.name)}
                </NavLink>
              ))}
            </div>

            {/* Mobile Menu Button */}
            <button
              type="button"
              className="lg:hidden p-2 hover:text-white transition-colors"
              style={{ color: '#e0e0e0' }}
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        <AnimatePresence>
          {mobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="lg:hidden border-t border-gray-700"
              style={{ backgroundColor: '#2a2a2a' }}
            >
              <div className="container-wide py-4 space-y-1">
                {navigation.slice(0, 2).map((item) => (
                  <NavLink
                    key={item.name}
                    to={item.href}
                    onClick={() => setMobileMenuOpen(false)}
                    className={({ isActive }) =>
                      `block px-4 py-3 text-base font-medium transition-colors ${
                        isActive
                          ? 'text-white'
                          : 'hover:text-white'
                      }`
                    }
                    style={({ isActive }) => ({
                      color: isActive ? '#ffffff' : '#e0e0e0'
                    })}
                  >
                    {t(item.name)}
                  </NavLink>
                ))}

                {/* Mobile Software Section — after About */}
                <MobileSoftwareMenu />

                {navigation.slice(2).map((item) => (
                  <NavLink
                    key={item.name}
                    to={item.href}
                    onClick={() => setMobileMenuOpen(false)}
                    className={({ isActive }) =>
                      `block px-4 py-3 text-base font-medium transition-colors ${
                        isActive
                          ? 'text-white'
                          : 'hover:text-white'
                      }`
                    }
                    style={({ isActive }) => ({
                      color: isActive ? '#ffffff' : '#e0e0e0'
                    })}
                  >
                    {t(item.name)}
                  </NavLink>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </nav>
    </header>
  );
}
